import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { ChromiumButton } from '@chancenwerk/client/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { type PresenceRecord, Status, UserPresenceRecord } from '@chancenwerk/shared/PresenceRecord.js';
import { EmployeePublicInformationAPI } from '@chancenwerk/shared/public-access/api/EmployeePublicInformationAPI.js';
import type { Mitarbeiter } from '@chancenwerk/shared/users/Mitarbeiter.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { switchMap } from 'rxjs';

@customElement('board-dialog-add-substitute-leko-course')
export class BoardDialogAddSubstituteLeKoCourse extends ChromiumDialog<string> {
  @property({ attribute: false }) kpfID = new RXController(this, '');
  @property({ attribute: false }) record: Maybe<PresenceRecord> = null;

  @state() private _lekos = new RXController(
    this,
    this.kpfID.observable.pipe(
      switchMap(async kpfID => {
        return EmployeePublicInformationAPI.getSubstituteLeKosKPF(kpfID)({
          vorname: 1,
          nachname: 1,
          handynummer: 1,
          guestAccount: 1,
        });
      }),
    ),
  );
  @state() private _selectedLeko = new RXController<Maybe<Mitarbeiter>>(this, null);

  _submitButtonRef: Ref<ChromiumButton> = createRef();

  static override get element_name() {
    return 'board-dialog-add-substitute-leko-course';
  }

  override heading = 'LeKo-Vertretung hinzufügen';

  override content() {
    if (this._lekos.isLoading) {
      return html`<cr-flex space="md" crossaxis-center>
        <cr-loader></cr-loader>
        <cr-text>LeKos werden gesucht...</cr-text>
      </cr-flex>`;
    }
    return html`<cr-flex space="md">
      <cr-dropdown-search-selection
        placeholder="LeKo"
        .search=${(search: Maybe<string>) => {
          return (this._lekos.value ?? []).filter(l => {
            if (!search) return true;
            return l.toString().toLowerCase().includes(search.toLowerCase());
          });
        }}
        .renderString=${(leko: Maybe<Mitarbeiter>) => (leko ?? '').toString()}
        @value-picked=${(e: ValueEvent<Mitarbeiter>) => {
          this._selectedLeko.next(e.detail.value);
        }}
      >
      </cr-dropdown-search-selection>
      <cr-button @click=${this._saveLeKo} ${ref(this._submitButtonRef)}>Erstellen</cr-button>
    </cr-flex> `;
  }

  private async _saveLeKo() {
    if (!this.record) {
      void ChromiumSnackbar.show('Kein Kurs gewählt!');
      return;
    }

    if (!this._selectedLeko.value) {
      void ChromiumSnackbar.show('Kein LeKo gewählt!');
      return;
    }

    if (this._submitButtonRef.value) {
      this._submitButtonRef.value.loading = true;
    }

    try {
      this.record.lekoRecords ??= [];
      this.record.lekoRecords.push(
        new UserPresenceRecord({
          userID: this._selectedLeko.value._id,
          user: this._selectedLeko.value,
          comment: 'LeKo-Vertretung',
          date: this.record.date,
          status: Status.PRESENT,
        }),
      );

      await this.close(this._selectedLeko.value._id);
    } catch (err: any) {
      void ChromiumSnackbar.show(err.message);
    } finally {
      if (this._submitButtonRef.value) {
        this._submitButtonRef.value.loading = false;
      }
    }
  }
}
